import cryptoIcon from '@raison/static/icons/bit-coin-line.svg';
import cashIcon from '@raison/static/icons/coins-line.svg';
import {Prose, Resource} from '@raison/types';
import {createAction} from '@reduxjs/toolkit';
import * as Array from 'effect/Array';
import {isString} from 'effect/Predicate';
import React from 'react';
import {Route, Routes, useNavigate} from 'react-router';
import {Currency, usePrimaryCurrency} from '~/entities/currencies';
import {
    DashboardAssetItem,
    DashboardCategory,
    DashboardCategoryLink,
    DashboardCategoryPortion,
    DashboardCategoryTotal,
} from '~/entities/dashboard-categories';
import {ProviderId} from '~/entities/operations-fiat';
import {TransactionsHistory} from '~/entities/transactions-history';
import {DashboardCategoryContext, DashboardCategoryScreen} from '~/features/dashboard-asset-pages';
import {bindTracedSelector} from '~/shared/store';
import {createUseResourceSuspense, useAppDispatch} from '~/shared/store/hooks';
import {
    AssetActions,
    AssetActionsRecord,
    AssetDetails,
    AssetDetailsProvider,
    AssetDetailsRoute,
    AssetHistory,
    AssetIcon,
    AssetTotal,
} from '../../../features/dashboard-asset-pages/ui/AssetDetailsPage';

const knownAccountCategories = {
    fiat: {title: 'Cash', color: '#76D1D7', icon: cashIcon},
    crypto: {title: 'Crypto', color: '#FEA45B', icon: cryptoIcon},
};

export const refreshAccounts = createAction('app/refreshAccounts');

export namespace AccountDashboardCategory {
    export const create = createAccountsDashboardCategory;

    export type AssetType = keyof typeof knownAccountCategories;
    export const isAssetType = (thing: unknown): thing is AssetType =>
        isString(thing) && thing in knownAccountCategories;

    export interface Asset {
        id: string | number;
        providerId: ProviderId | null;
        icon: string;
        title: string;
        balance: Prose;
        balanceInPrimary: Prose;
        currency: Currency;
        actions: AssetActionsRecord;
        history: TransactionsHistory.Filters | null;
        summary: Array<{label: string; value: string}>;
    }

    export interface Provider {
        id: ProviderId;
        name: string;
    }
}

function createAccountsDashboardCategory(
    slug: AccountDashboardCategory.AssetType,
    selectTotal: AppSelector<Resource<DashboardCategoryTotal>>,
    selectAssets: AppSelector<Resource<AccountDashboardCategory.Asset[]>>,
    selectProviders: AppSelector<Resource<AccountDashboardCategory.Provider[]>>
): DashboardCategory {
    const {color, icon, title} = knownAccountCategories[slug];
    const context: DashboardCategoryContext = {
        color: color,
        kindPlural: Prose.make('accounts'),
        kindSingular: Prose.make('account'),
    };

    const useCategoryTotalResource = bindTracedSelector(selectTotal);
    const useCategoryTotalSuspense = createUseResourceSuspense(selectTotal);

    const useCategoryAssetsResource = bindTracedSelector((state: AppModel) =>
        Resource.all(selectAssets(state), selectProviders(state)).mapSuccess(([accounts, providers]) =>
            Object.entries(Array.groupBy(accounts, a => String(a.providerId ?? ''))).map(([providerId, accounts]) => {
                const provider = providerId && providers?.find(p => p.id.toString() === providerId);
                return {
                    title: !provider ? null : Prose.make(provider.name),
                    children: accounts,
                };
            })
        )
    );

    const useAccountById = createUseResourceSuspense((state, assetId: string) =>
        selectAssets(state).mapSuccess(as => as.find(a => a.id == assetId))
    );

    function AccountItem(props: {asset: AccountDashboardCategory.Asset}) {
        const navigate = useNavigate();
        return (
            <DashboardAssetItem
                onClick={() => navigate(`./${props.asset.id}`)}
                icon={props.asset.icon}
                title={props.asset.title}
                detail={props.asset.balance}
                amount={props.asset.balanceInPrimary}
            />
        );
    }
    AccountItem.Skeleton = DashboardAssetItem.Skeleton;

    const AccountDetailsProvider: AssetDetailsProvider = props => {
        const primary = usePrimaryCurrency();
        const account = useAccountById(props.assetId);
        if (!account) {
            return props.notFound;
        }
        const {balance, balanceInPrimary, summary, icon, history, actions, title, currency} = account;
        return props.children({
            title: title,
            Icon: <AssetIcon src={icon} />,
            Totals: (
                <AssetTotal balance={balanceInPrimary} details={primary.code !== currency.code ? balance : undefined} />
            ),
            ...(history && {History: <AssetHistory filters={history} />}),
            ...(Object.values(actions).some(Boolean) ? {Actions: <AssetActions actions={actions} />} : null),
            ...(Object.values(summary).some(Boolean) ? {Details: <AssetDetails sections={[summary]} />} : null),
        });
    };

    return {
        categorySlug: slug,
        CategoryLink({to}) {
            const mapped = useCategoryTotalResource();
            return <DashboardCategoryLink to={to} icon={icon} title={title} total={mapped} />;
        },
        CategoryPage() {
            const dispatch = useAppDispatch();
            const total = useCategoryTotalResource();
            const assets = useCategoryAssetsResource();
            const page = (
                <DashboardCategoryScreen.Awaiting
                    Heading={DashboardCategoryScreen.Heading}
                    Item={AccountItem}
                    title={title}
                    total={total}
                    assets={assets}
                    refresh={() => dispatch(refreshAccounts())}
                />
            );
            return (
                <Routes>
                    <Route index element={page} />
                    <Route path=':assetId' element={<AssetDetailsRoute provider={AccountDetailsProvider} />} />
                </Routes>
            );
        },
        CategoryContextProvider({children}) {
            return <DashboardCategoryContext.Provider value={context} children={children} />;
        },
        useCategoryPortion(): DashboardCategoryPortion {
            const total = useCategoryTotalSuspense();
            return {
                categoryName: Prose.make(title),
                categoryColor: color,
                categoryPercent: total.percent,
            };
        },
    };
}
